import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, Save, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin matrix editor for tier-based menu access.
 *
 * Rows = modules (from MODULE_REGISTRY on backend).
 * Cols = every tier slug (default + custom).
 * Each cell is a checkbox toggling whether that tier sees the module.
 *
 * `always_on` modules render with locked checkboxes (always granted to every tier).
 */
export default function TierAccessMatrix() {
  const { getAuthHeader } = useAuth();
  const [modules, setModules] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [access, setAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/tier-access`, { headers: getAuthHeader() });
      setModules(res.data.modules || []);
      setTiers(res.data.tiers || []);
      setAccess(res.data.access || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tier access matrix');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleCell = (moduleId, tierSlug) => {
    setAccess((prev) => {
      const current = new Set(prev[moduleId] || []);
      if (current.has(tierSlug)) current.delete(tierSlug);
      else current.add(tierSlug);
      return { ...prev, [moduleId]: Array.from(current) };
    });
  };

  const setRow = (moduleId, mode) => {
    setAccess((prev) => ({
      ...prev,
      [moduleId]: mode === 'all' ? [...tiers] : [],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/tier-access`,
        { access },
        { headers: getAuthHeader() }
      );
      toast.success('Tier access matrix saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Loading access matrix…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tier-access-matrix">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-playfair font-bold text-foreground mb-2 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary" /> Tier Access Manager
          </h2>
          <p className="text-muted-foreground text-sm max-w-2xl">
            Grant each membership tier access to specific menu modules. Locked modules
            appear greyed out for that tier with an "Upgrade" CTA. Always-on modules
            (Dashboard, Profile) cannot be removed and are granted to every tier automatically.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="reload-matrix-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Reload
          </Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-matrix-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="wm-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-4 font-semibold text-foreground sticky left-0 bg-card z-10">
                Module
              </th>
              {tiers.map((t) => (
                <th key={t} className="p-4 font-semibold text-foreground capitalize text-center min-w-[110px]">
                  {t}
                </th>
              ))}
              <th className="p-4 text-right text-xs uppercase tracking-widest text-muted-foreground">
                Quick set
              </th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => {
              const allowedSet = new Set(access[m.id] || []);
              return (
                <tr
                  key={m.id}
                  className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                  data-testid={`matrix-row-${m.id}`}
                >
                  <td className="p-4 sticky left-0 bg-card z-10">
                    <div className="flex items-center gap-2">
                      {m.always_on && <Lock className="w-3.5 h-3.5 text-gold" />}
                      <span className="font-medium text-foreground">{m.label}</span>
                    </div>
                    <span className="text-xs text-muted-foreground font-mono">{m.id}</span>
                    {m.always_on && (
                      <p className="text-[10px] uppercase tracking-widest text-gold mt-1">Always on</p>
                    )}
                  </td>
                  {tiers.map((t) => {
                    const checked = m.always_on ? true : allowedSet.has(t);
                    return (
                      <td key={t} className="p-4 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={m.always_on}
                          onChange={() => toggleCell(m.id, t)}
                          className="w-5 h-5 accent-gold cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                          data-testid={`cell-${m.id}-${t}`}
                          aria-label={`${m.label} access for ${t}`}
                        />
                      </td>
                    );
                  })}
                  <td className="p-4 text-right">
                    {!m.always_on && (
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          onClick={() => setRow(m.id, 'all')}
                          className="text-xs uppercase tracking-widest text-emerald-500 hover:text-emerald-400"
                          data-testid={`row-all-${m.id}`}
                        >
                          All
                        </button>
                        <span className="text-muted-foreground">·</span>
                        <button
                          type="button"
                          onClick={() => setRow(m.id, 'none')}
                          className="text-xs uppercase tracking-widest text-rose-500 hover:text-rose-400"
                          data-testid={`row-none-${m.id}`}
                        >
                          None
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-muted-foreground bg-muted/20 border border-border rounded-md p-3">
        <strong className="text-foreground">Heads up:</strong> changes apply on a member's
        next page load. Their navigation and module cards will refresh once they revisit
        the dashboard. Members with zero access to a module will see a greyed-out card
        with an "Upgrade to <em>X</em>" call-to-action that links to <code>/pricing</code>.
      </div>
    </div>
  );
}
